import type { AdminInvite } from '@/api/endpoints/admin-invites';
import { confirm } from '@/components/ui/confirmation-dialog';
import { TextInput } from '@/components/ui/text-input';
import Ionicons from '@expo/vector-icons/Ionicons';
import { Send } from 'lucide-react-native';
import React, { useState } from 'react';
import { Platform, Pressable, Text, View } from 'react-native';

interface AdminInviteRowProps {
  invite: AdminInvite;
  onSendEmail: (code: string) => void;
  onDelete: (code: string) => void;
  onUpdateEmail?: (code: string, email: string | null) => void;
}

function getStatus(invite: AdminInvite): 'active' | 'used' | 'expired' {
  if (invite.usedBy) return 'used';
  if (invite.expiresAt && new Date(invite.expiresAt) < new Date()) return 'expired';
  return 'active';
}

const statusBadgeClass: Record<string, string> = {
  active: 'bg-primary',
  used: 'bg-muted-foreground',
  expired: 'bg-error',
};

export function AdminInviteRow({ invite, onSendEmail, onDelete, onUpdateEmail }: AdminInviteRowProps) {
  const status = getStatus(invite);
  const [editingEmail, setEditingEmail] = useState(false);
  const [emailDraft, setEmailDraft] = useState(invite.email ?? '');

  const statusLabels = {
    active: 'Active',
    used: 'Used',
    expired: 'Expired',
  };

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: 'Delete invite?',
      description: `Delete invite code ${invite.code}?`,
      variant: 'destructive',
      confirmLabel: 'Delete',
    });
    if (confirmed) {
      onDelete(invite.code);
    }
  };

  const handleSendEmail = async () => {
    const confirmed = await confirm({
      title: 'Send invite?',
      description: `Send invite to ${invite.email}?`,
      variant: 'info',
      icon: Send,
      confirmLabel: 'Send',
      confirmVariant: 'primary',
    });
    if (confirmed) {
      onSendEmail(invite.code);
    }
  };

  return (
    <View
      className="border-b border-border"
      style={{ paddingVertical: 12, paddingHorizontal: 16 }}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 6 }}>
        <Text
          className="text-foreground"
          style={{
            fontSize: 16,
            fontWeight: '700',
            fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
            letterSpacing: 1,
          }}
        >
          {invite.code}
        </Text>
        <View
          className={statusBadgeClass[status]}
          style={{
            borderRadius: 4,
            paddingHorizontal: 6,
            paddingVertical: 1,
            marginLeft: 8,
          }}
        >
          <Text className="text-xs font-bold" style={{ color: '#fff' }}>
            {statusLabels[status]}
          </Text>
        </View>
      </View>

      {editingEmail && status === 'active' ? (
        <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 2, gap: 6 }}>
          <TextInput
            size="sm"
            value={emailDraft}
            onChangeText={setEmailDraft}
            placeholder="email@example.com"
            autoCapitalize="none"
            autoCorrect={false}
            keyboardType="email-address"
            inputContainerStyle={{ flex: 1 }}
          />
          <Pressable
            onPress={() => {
              const trimmed = emailDraft.trim();
              onUpdateEmail?.(invite.code, trimmed || null);
              setEditingEmail(false);
            }}
            accessibilityRole="button"
            accessibilityLabel="Save email"
            className="bg-primary"
            style={{
              borderRadius: 6,
              paddingHorizontal: 10,
              paddingVertical: 5,
              minHeight: 44,
            }}
          >
            <Text className="text-sm font-semibold text-background">
              Save
            </Text>
          </Pressable>
          <Pressable
            onPress={() => {
              setEmailDraft(invite.email ?? '');
              setEditingEmail(false);
            }}
            accessibilityRole="button"
            accessibilityLabel="Cancel editing"
            className="bg-surface-secondary"
            style={{
              borderRadius: 6,
              paddingHorizontal: 10,
              paddingVertical: 5,
              minHeight: 44,
            }}
          >
            <Text className="text-sm font-semibold text-muted-foreground">
              Cancel
            </Text>
          </Pressable>
        </View>
      ) : (
        <Pressable
          disabled={status !== 'active' || !onUpdateEmail}
          onPress={() => {
            setEmailDraft(invite.email ?? '');
            setEditingEmail(true);
          }}
        >
          <Text className="text-sm text-muted-foreground" style={{ marginBottom: 2 }}>
            {invite.email || 'No email'}
          </Text>
        </Pressable>
      )}

      <View style={{ flexDirection: 'row', gap: 12, marginBottom: 2 }}>
        {invite.creator && (
          <Text className="text-xs text-muted-foreground">
            Created by {invite.creator.name}
          </Text>
        )}
        {invite.user && (
          <Text className="text-xs text-muted-foreground">
            Used by {invite.user.name}
          </Text>
        )}
      </View>

      <View style={{ flexDirection: 'row', gap: 12, marginBottom: 8 }}>
        <Text className="text-xs text-muted-foreground">
          Created {new Date(invite.createdAt).toLocaleDateString()}
        </Text>
        {invite.expiresAt && (
          <Text className="text-xs text-muted-foreground">
            Expires {new Date(invite.expiresAt).toLocaleDateString()}
          </Text>
        )}
      </View>

      <View style={{ flexDirection: 'row', gap: 8 }}>
        {invite.email && status === 'active' && (
          <Pressable
            onPress={handleSendEmail}
            accessibilityRole="button"
            accessibilityLabel="Send invite email"
            className="bg-primary"
            style={{
              flex: 1,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 8,
              paddingVertical: 8,
              gap: 4,
            }}
          >
            <Ionicons name="mail-outline" size={16} className="text-background" />
            <Text className="text-sm font-semibold text-background">
              Send Email
            </Text>
          </Pressable>
        )}

        {status !== 'used' && (
          <Pressable
            onPress={handleDelete}
            accessibilityRole="button"
            accessibilityLabel="Delete invite"
            className="bg-surface-secondary"
            style={{
              flex: 1,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 8,
              paddingVertical: 8,
              gap: 4,
            }}
          >
            <Ionicons name="trash-outline" size={16} className="text-error" />
            <Text className="text-sm font-semibold text-error">
              Delete
            </Text>
          </Pressable>
        )}
      </View>
    </View>
  );
}
